import {
  ChangeDetectorRef, Component, input, signal, inject, DestroyRef, computed, effect,
  InputSignalWithTransform, booleanAttribute
} from '@angular/core';

import { TranslatePipe } from '../../pipes/translate.pipe';
import { LoadingIndicatorPosition } from '../loading-indicator/loading-indicator.component';
import { DetailedSpinnerComponent } from './detailed-spinner.component';
import type { EffectCleanupRegisterFn, InputSignal, Signal, WritableSignal } from '@angular/core';

@Component({
  selector: 'td-wb-detailed-loading-indicator',
  templateUrl: 'detailed-loading-indicator.component.html',
  styleUrls: ['detailed-loading-indicator.component.scss'],
  imports: [DetailedSpinnerComponent, TranslatePipe],
  host: {
    '[class.td-wb-detailed-loading-spinner--top]': 'isTop()',
    '[attr.aria-live]': 'ariaLive()'
  }
})
export class DetailedLoadingIndicatorComponent {
  private static readonly DISPLAY_DELAY_MS: number = 300;
  private static readonly REMOVE_DELAY_MS: number = 12000;

  spinnerSize: InputSignal<'medium' | 'small' | 'large'> = input<'medium' | 'small' | 'large'>('medium');

  readonly position: InputSignal<LoadingIndicatorPosition | undefined> = input<LoadingIndicatorPosition | undefined>(undefined);
  readonly displayDelay: InputSignal<number | undefined> = input<number | undefined>(DetailedLoadingIndicatorComponent.DISPLAY_DELAY_MS);
  readonly removeDelay: InputSignal<number | undefined> = input<number | undefined>(DetailedLoadingIndicatorComponent.REMOVE_DELAY_MS);
  readonly ariaLive: InputSignal<string> = input<string>('polite');
  readonly hasHeader: InputSignalWithTransform<boolean, unknown> = input(false, { transform: booleanAttribute });
  readonly hasDesc: InputSignalWithTransform<boolean, unknown> = input(false, { transform: booleanAttribute });

  readonly displaySpinner: WritableSignal<boolean> = signal<boolean>(false);

  private displaySpinnerTimer: ReturnType<typeof setTimeout> | undefined;
  private removeSpinnerTimer: ReturnType<typeof setTimeout> | undefined;

  isTop: Signal<boolean> = computed((): boolean => this.position() === 'top');

  private readonly effectiveDisplayDelay: Signal<number> = computed((): number => {
    return this.displayDelay() ?? DetailedLoadingIndicatorComponent.DISPLAY_DELAY_MS;
  });

  readonly effectiveRemoveDelay: Signal<number> = computed((): number => {
    return this.removeDelay() ?? DetailedLoadingIndicatorComponent.REMOVE_DELAY_MS;
  });

  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    this.destroyRef.onDestroy((): void => {
      this.clearTimers();
    });

    // effect: manages display/remove timers in response to delay signal changes —
    // setTimeout/clearTimeout are imperative browser APIs; onCleanup cancels any
    // in-flight timer when the effect re-runs or the component is destroyed.
    effect((onCleanup: EffectCleanupRegisterFn): void => {
      this.clearTimers();
      this.displaySpinner.set(false);

      const displayDelay = this.effectiveDisplayDelay();
      const removeDelay = this.effectiveRemoveDelay();

      if (displayDelay !== 0) {
        this.displaySpinnerTimer = setTimeout((): void => {
          this.displaySpinnerTimer = undefined;
          this.showSpinner(removeDelay);
        }, displayDelay);
      } else {
        this.showSpinner(removeDelay);
      }

      onCleanup((): void => {
        this.clearTimers();
      });
    });
  }

  private clearTimers(): void {
    if (this.displaySpinnerTimer !== undefined) {
      clearTimeout(this.displaySpinnerTimer);
      this.displaySpinnerTimer = undefined;
    }
    if (this.removeSpinnerTimer !== undefined) {
      clearTimeout(this.removeSpinnerTimer);
      this.removeSpinnerTimer = undefined;
    }
  }

  private showSpinner(removeDelay: number): void {
    this.displaySpinner.set(true);
    this.cdr.markForCheck();
    this.removeSpinnerTimer = setTimeout((): void => {
      this.displaySpinner.set(false);
      this.removeSpinnerTimer = undefined;
      this.cdr.markForCheck();
    }, removeDelay);
  }
}

